import { createContext, useContext, useRef } from 'react';

import {
	Box,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash-es';

import { Api } from '@/api';
import { useAppQuery } from '@/app-react-query';
import { LoadingContainer } from '@/components/ui';
import { isFormEngineV2 } from '@/forms/core/FormEngineV2';
import FormEngineV2Container from '@/forms/core/FormEngineV2Container';
import { formIsCbwById } from '@/utils';

import CreateNoteModal from '../Notes/components/CreateNoteModal';

import FormProvider, { FormProviderProps } from './';
import { ViewNotesButton } from './ViewNotesButton';

const FormReviewModalContext = createContext(false);
/** returns true if `FormReviewModal` is open */
export const useIsFormReviewModalOpen = () => useContext(FormReviewModalContext);

export type ModalInfoProps = FormProviderProps & {
	Component?: string;
	date?: string;
	isDisabled_?: boolean;
	score?: number;
	version?: number;
};

type FormModalProps = {
	isOpen: boolean;
	modalInfo: ModalInfoProps;
	onClose: VoidFunction;
};

export default function FormModal({ modalInfo, onClose, isOpen }: FormModalProps) {
	const notesQuery = useAppQuery(
		['Api.forms.getResponseNotes', { response_id: modalInfo?.response_id }],
		() => Api.forms.getResponseNotes(modalInfo?.response_id as number),
		{
			enabled: Boolean(isOpen && modalInfo?.response_id),
		}
	);
	const printableRef = useRef<HTMLDivElement>(null);

	const printModalContent = () => {
		const content = printableRef.current?.innerHTML;
		if (!content) {
			console.warn('No printable content found');
			return;
		}

		// 1. Create an invisible iframe
		const iframe = document.createElement('iframe');
		iframe.style.position = 'fixed';
		iframe.style.width = '0';
		iframe.style.height = '0';
		iframe.style.border = '0';
		document.body.appendChild(iframe);

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const doc = iframe.contentWindow!.document;

		// 2. Copy current document styles for a near-identical look
		const styles = Array.from(document.styleSheets)
			.map((sheet) => {
				try {
					return Array.from(sheet.cssRules)
						.map((rule) => rule.cssText)
						.join('\n');
				} catch (e) {
					// Ignore cross-origin stylesheets
					return '';
				}
			})
			.join('\n');

		// 3. Write content to the iframe
		doc.open();
		doc.write(`
    		<html>
    		  <head>
    	    <title>Print</title>
    	    <style>
    	      ${styles}
    	      body { margin: 1rem; font-family: sans-serif; }
    	    </style>
    	  </head>
    	  <body>
    	    ${content}
    	  </body>
    	</html>
  `);
		doc.close();

		// 4. Wait for iframe to load, then print
		iframe.onload = () => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			iframe.contentWindow!.focus();
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			iframe.contentWindow!.print();
			// Clean up
			document.body.removeChild(iframe);
		};
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				size={formIsCbwById(modalInfo?.form_id as number) ? 'full' : '4xl'}
				onClose={onClose}
			>
				<FormReviewModalContext.Provider value={true}>
					<ModalOverlay />
					<ModalContent>
						<div ref={printableRef}>
							<LoadingContainer isLoading={notesQuery.isLoading} />

							<Box visibility={notesQuery.isLoading ? 'hidden' : undefined}>
								<ModalHeader alignItems="start" as={VStack} mb={0} pb={0} spacing="1">
									{modalInfo.isDisabled_ && (
										<Text fontSize="md" fontWeight="bold">
											Form Review: {modalInfo.date}
										</Text>
									)}

									{!!modalInfo.score && (
										<Text fontSize="md" fontWeight="normal">
											Score: {modalInfo?.score}
										</Text>
									)}

									{!!modalInfo.client_id && (
										<CreateNoteModal
											responseId={modalInfo?.response_id}
											onSuccess={() => notesQuery.refetch()}
										/>
									)}
									{!isEmpty(notesQuery.data) && (
										<ViewNotesButton notes={notesQuery.data as Note[]} />
									)}
								</ModalHeader>
								<ModalCloseButton />
								<ModalBody>
									{isFormEngineV2({ form_data: modalInfo }) ? (
										<FormEngineV2Container response_id={modalInfo.response_id} />
									) : (
										<FormProvider
											client_id={modalInfo.client_id}
											form_id={modalInfo.form_id}
											isDisabled_={modalInfo.isDisabled_}
											response_id={modalInfo.response_id}
										/>
									)}
								</ModalBody>
								<ModalFooter>
									<Button onClick={printModalContent}>Print</Button>
									<Button onClick={onClose}>Close</Button>
								</ModalFooter>
							</Box>
						</div>
					</ModalContent>
				</FormReviewModalContext.Provider>
			</Modal>
		</>
	);
}
